import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Save, Eye, CheckCircle, Send, Calendar, Archive, ShieldCheck, Wand2, Trash2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";

const CATEGORIES = ["fintech","payments","banking","cards","infrastructure","digital-assets","regulation","companies","insights"];
const TYPES = ["Breaking News","News","Analysis","Explainer","Opinion","Interview","Company Profile","Research","Industry Brief","Sponsored Content","Partner Content"];

export default function ArticleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tab, setTab] = useState("editorial");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [factBusy, setFactBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/articles/${id}`).then((r) => setA(r.data)).catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => {
    load();
    api.get("/authors").then((r) => setAuthors(r.data || []));
    api.get("/companies").then((r) => setCompanies(r.data || []));
  }, [load]);

  if (!a) return <AdminLayout title="Article"><div className="text-admmuted">Loading…</div></AdminLayout>;
  const set = (k, v) => setA({ ...a, [k]: v });

  const save = async (silent) => {
    setSaving(true);
    const { status, publication_date, scheduled_for, ...content } = a;
    try { await api.put(`/admin/articles/${id}`, content); if (!silent) toast.success("Saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const setStatus = async (status, extra = {}) => {
    await save(true);
    try { await api.post(`/admin/articles/${id}/status`, { status, ...extra }); setA((prev) => prev ? { ...prev, status } : prev); toast.success(`Marked ${status.replace("_"," ")}`); await load(); }
    catch { toast.error("Failed"); }
  };
  const factCheck = async () => {
    await save(true); setFactBusy(true);
    try { await api.post(`/agent/articles/${id}/factcheck`); toast.success("Fact check complete"); load(); }
    catch { toast.error("Fact check failed"); } finally { setFactBusy(false); }
  };
  const rewrite = async () => {
    setFactBusy(true);
    try {
      await api.post(`/agent/articles/${id}/rewrite`, { instruction: "" });
      toast.info("AI Rewrite started — grounding the draft in verified evidence. This can take a minute…");
    } catch {
      toast.error("Rewrite failed to start"); setFactBusy(false); return;
    }
    const started = Date.now();
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/articles/${id}`);
        if (data.rewrite_status === "running" && Date.now() - started < 240000) {
          setTimeout(poll, 4000); return;
        }
        setA(data);
        if (data.rewrite_status === "error") toast.error("Rewrite failed");
        else if (data.rewrite_status === "running") toast.error("Rewrite is taking longer than expected — check back shortly");
        else toast.success(`Rewritten — Fact Check verdict: ${(data.fact_check?.verdict) || "updated"}`);
      } catch {
        toast.error("Could not read rewrite status");
      } finally {
        setFactBusy(false);
      }
    };
    setTimeout(poll, 5000);
  };
  const remove = async () => { if (!window.confirm("Delete this article?")) return; await api.delete(`/admin/articles/${id}`); nav("/admin/articles"); };
  const schedule = async () => { const dt = window.prompt("Schedule for (ISO, e.g. 2026-07-01T09:00:00Z):"); if (dt) setStatus("scheduled", { scheduled_for: dt }); };
  const publish = async () => {
    const v = (a.fact_check?.verdict || "").toUpperCase();
    if (v === "FAIL") {
      if (!window.confirm("⚠️ Fact Check verdict is FAIL — the AI flagged unverified, contradicted or unsupported claims in this draft.\n\nPublishing overrides the editorial safety gate and takes full responsibility for accuracy. This is strongly discouraged; consider 'AI Rewrite' first.\n\nOverride and publish anyway?")) return;
      return setStatus("published", { override: true });
    }
    if (v === "NEEDS REVIEW") {
      if (!window.confirm("Fact Check verdict is NEEDS REVIEW. Material claims require human verification. Override and publish anyway?")) return;
      return setStatus("published", { override: true });
    }
    setStatus("published");
  };

  const inp = "w-full bg-admbg border border-admborder px-3 py-2 text-admtext text-sm outline-none focus:border-cyan";
  const lbl = "text-[10px] font-bold uppercase tracking-widest text-admtext2 mb-1 block";

  return (
    <AdminLayout title="Article Editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button onClick={() => nav("/admin/articles")} className="flex items-center gap-1.5 text-admtext2 text-sm hover:text-admtext"><ArrowLeft size={15} /> Articles</button>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => save(false)} disabled={saving} data-testid="editor-save" className="flex items-center gap-1.5 border border-admborder text-admtext px-3 py-1.5 text-xs uppercase tracking-wider hover:border-cyan"><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
          <button onClick={() => setPreview(!preview)} data-testid="editor-preview" className="flex items-center gap-1.5 border border-admborder text-admtext px-3 py-1.5 text-xs uppercase tracking-wider hover:border-cyan"><Eye size={13} /> Preview</button>
          <button onClick={factCheck} disabled={factBusy} data-testid="editor-factcheck" className="flex items-center gap-1.5 border border-admborder text-admtext px-3 py-1.5 text-xs uppercase tracking-wider hover:border-amber"><ShieldCheck size={13} /> Fact Check</button>
          <button onClick={rewrite} disabled={factBusy} data-testid="editor-rewrite" className="flex items-center gap-1.5 border border-admborder text-admtext px-3 py-1.5 text-xs uppercase tracking-wider hover:border-cyan"><Wand2 size={13} /> AI Rewrite</button>
          <button onClick={() => setStatus("awaiting_review")} data-testid="editor-ready" className="flex items-center gap-1.5 border border-amber text-amber px-3 py-1.5 text-xs uppercase tracking-wider"><CheckCircle size={13} /> Ready for Review</button>
          <button onClick={schedule} data-testid="editor-schedule" className="flex items-center gap-1.5 border border-admborder text-admtext px-3 py-1.5 text-xs uppercase tracking-wider hover:border-cyan"><Calendar size={13} /> Schedule</button>
          <button onClick={publish} data-testid="editor-publish" className="flex items-center gap-1.5 bg-emerald text-admbg px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"><Send size={13} /> Approve & Publish</button>
          {a.status === "published" && <button onClick={() => setStatus("draft")} className="border border-admborder text-admtext2 px-3 py-1.5 text-xs uppercase tracking-wider">Unpublish</button>}
          <button onClick={() => setStatus("archived")} className="flex items-center gap-1.5 border border-admborder text-admmuted px-3 py-1.5 text-xs uppercase tracking-wider hover:border-crimson"><Archive size={13} /></button>
          <button onClick={remove} className="text-crimson hover:opacity-80"><Trash2 size={15} /></button>
        </div>
      </div>

      {preview ? (
        <div className="bg-paper text-ink p-8 max-w-3xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-widest text-crimson">{a.category}</div>
          <h1 className="font-serif font-bold text-4xl mt-2">{a.headline}</h1>
          <p className="text-lg text-ink2 mt-3">{a.subtitle}</p>
          {a.featured_image && <img src={a.featured_image} className="w-full mt-6" alt="" />}
          <div className="prose-editorial mt-6" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(a.body || "")) }} />
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: editorial */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex gap-1">
            {["editorial","seo","meta"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs uppercase tracking-wider border capitalize ${tab === t ? "border-cyan text-cyan" : "border-admborder text-admtext2"}`}>{t}</button>
            ))}
          </div>

          {tab === "editorial" && (
            <div className="bg-admcard border border-admborder p-5 space-y-4">
              <div><label className={lbl}>Headline</label><input className={inp} value={a.headline || ""} onChange={(e) => set("headline", e.target.value)} data-testid="editor-headline" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Alternative Headline</label><input className={inp} value={a.alt_headline || ""} onChange={(e) => set("alt_headline", e.target.value)} /></div>
                <div><label className={lbl}>Slug</label><input className={inp} value={a.slug || ""} onChange={(e) => set("slug", e.target.value)} /></div>
              </div>
              <div><label className={lbl}>Standfirst / Subtitle</label><textarea rows={2} className={inp} value={a.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></div>
              <div><label className={lbl}>Why This Matters</label><textarea rows={2} className={inp} value={a.why_this_matters || ""} onChange={(e) => set("why_this_matters", e.target.value)} /></div>
              <div><label className={lbl}>Body (Markdown)</label><textarea rows={20} className={`${inp} font-mono text-xs leading-relaxed`} value={a.body || ""} onChange={(e) => set("body", e.target.value)} data-testid="editor-body" /></div>
            </div>
          )}

          {tab === "seo" && (
            <div className="bg-admcard border border-admborder p-5 space-y-4">
              <div><label className={lbl}>SEO Title</label><input className={inp} value={a.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} /></div>
              <div><label className={lbl}>Meta Description</label><textarea rows={2} className={inp} value={a.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} /></div>
              <div><label className={lbl}>Keywords (comma separated)</label><input className={inp} value={(a.keywords||[]).join(", ")} onChange={(e) => set("keywords", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} /></div>
              <div><label className={lbl}>Canonical URL</label><input className={inp} value={a.canonical_url || ""} onChange={(e) => set("canonical_url", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>OG Title</label><input className={inp} value={a.og_title || ""} onChange={(e) => set("og_title", e.target.value)} /></div>
                <div><label className={lbl}>OG Image</label><input className={inp} value={a.og_image || ""} onChange={(e) => set("og_image", e.target.value)} /></div>
              </div>
              <div><label className={lbl}>OG Description</label><textarea rows={2} className={inp} value={a.og_description || ""} onChange={(e) => set("og_description", e.target.value)} /></div>
            </div>
          )}

          {tab === "meta" && (
            <div className="bg-admcard border border-admborder p-5 space-y-4">
              <div><label className={lbl}>Featured Image URL</label><input className={inp} value={a.featured_image || ""} onChange={(e) => set("featured_image", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Image Caption</label><input className={inp} value={a.image_caption || ""} onChange={(e) => set("image_caption", e.target.value)} /></div>
                <div><label className={lbl}>Image Credit</label><input className={inp} value={a.image_credit || ""} onChange={(e) => set("image_credit", e.target.value)} /></div>
              </div>
              <div><label className={lbl}>Source URLs (one per line)</label><textarea rows={3} className={inp} value={(a.source_urls||[]).join("\n")} onChange={(e) => set("source_urls", e.target.value.split("\n").map(s=>s.trim()).filter(Boolean))} /></div>
              <div><label className={lbl}>Company References</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {companies.map((c) => {
                    const on = (a.company_refs||[]).includes(c.slug);
                    return <button key={c.id} onClick={() => set("company_refs", on ? a.company_refs.filter(s=>s!==c.slug) : [...(a.company_refs||[]), c.slug])}
                      className={`text-xs px-2 py-1 border ${on ? "border-cyan text-cyan" : "border-admborder text-admtext2"}`}>{c.name}</button>;
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: settings + fact check */}
        <div className="space-y-4">
          <div className="bg-admcard border border-admborder p-5 space-y-3">
            <div><label className={lbl}>Status</label><div className="text-cyan text-sm font-mono capitalize">{(a.status||"").replace("_"," ")}</div></div>
            <div><label className={lbl}>Category</label><select className={inp} value={a.category || ""} onChange={(e) => set("category", e.target.value)}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className={lbl}>Content Type</label><select className={inp} value={a.content_type || ""} onChange={(e) => set("content_type", e.target.value)}>{TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label className={lbl}>Author</label><select className={inp} value={a.author_id || ""} onChange={(e) => set("author_id", e.target.value)}><option value="">—</option>{authors.map(au => <option key={au.id} value={au.id}>{au.name}</option>)}</select></div>
            <div><label className={lbl}>Tags (comma separated)</label><input className={inp} value={(a.tags||[]).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} /></div>
            <div className="flex items-center justify-between pt-2 border-t border-admborder">
              <label className={lbl + " !mb-0"}>Sponsored</label>
              <button onClick={() => set("sponsored", !a.sponsored)} className={`w-10 h-5 rounded-full relative ${a.sponsored ? "bg-gold" : "bg-admborder"}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${a.sponsored ? "left-5" : "left-0.5"}`} /></button>
            </div>
            {a.sponsored && <div><label className={lbl}>Sponsor</label><input className={inp} value={a.sponsor || ""} onChange={(e) => set("sponsor", e.target.value)} /></div>}
          </div>

          {/* Fact check report */}
          <div className="bg-admcard border border-admborder p-5" data-testid="fact-check-report">
            <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Fact Check Report</h3>
            {a.fact_check ? <FactCheckReport fc={a.fact_check} /> : <p className="text-admmuted text-xs">No fact check yet. Run "Fact Check" to generate a report.</p>}
          </div>
        </div>
      </div>)}
    </AdminLayout>
  );
}

function FCList({ title, items, color }) {
  if (!items?.length) return null;
  return <div><div className={`uppercase tracking-wider font-bold ${color} mb-1`}>{title}</div><ul className="space-y-1 text-admtext2 list-disc pl-4">{items.map((it,i) => <li key={`${title}-${i}`}>{typeof it === "string" ? it : JSON.stringify(it)}</li>)}</ul></div>;
}

const VERDICTS = {
  "PASS": "bg-emerald/15 text-emerald border-emerald",
  "PASS WITH NOTES": "bg-cyan/15 text-cyan border-cyan",
  "NEEDS REVIEW": "bg-amber/15 text-amber border-amber",
  "FAIL": "bg-crimson/15 text-crimson border-crimson",
};
const CLS_COLOR = {
  "VERIFIED": "text-emerald", "SUPPORTED": "text-cyan", "UNVERIFIED": "text-amber",
  "CONTRADICTED": "text-crimson", "EDITORIAL/ANALYSIS": "text-admtext2",
};

function ClaimRow({ claim, cls, sources, note, badge }) {
  return (
    <li className="border-l-2 border-admborder pl-2 py-0.5">
      <div className="flex items-start gap-1.5">
        <span className={`shrink-0 uppercase tracking-wider font-bold text-[10px] ${CLS_COLOR[(cls||"").toUpperCase()] || "text-admtext2"}`}>{badge || cls}</span>
        <span className="text-admtext2">{claim}</span>
      </div>
      {note ? <div className="text-admmuted italic mt-0.5">{note}</div> : null}
      {sources?.length ? <div className="text-admmuted mt-0.5 break-all">{sources.map((s,i)=><div key={(typeof s === "string" ? s : s.url) || i}>↳ {typeof s === "string" ? s : (s.url || JSON.stringify(s))}</div>)}</div> : null}
    </li>
  );
}

function FactCheckReport({ fc }) {
  const V = (fc.verdict || "").toUpperCase();
  const isNew = fc.claim_by_claim !== undefined || fc.core_claims_status !== undefined || VERDICTS[V];
  if (!isNew) {
    // Legacy fact-check format fallback
    return (
      <div className="space-y-3 text-xs">
        <div><span className={`px-2 py-0.5 uppercase tracking-wider font-bold ${fc.verdict === "pass" ? "bg-emerald/15 text-emerald" : "bg-amber/15 text-amber"}`}>{fc.verdict === "pass" ? "Verified" : "Needs Review"}</span></div>
        <FCList title="Verified Facts" items={fc.verified_facts} color="text-emerald" />
        <FCList title="Potential Uncertainty" items={fc.potential_uncertainty} color="text-amber" />
        <FCList title="Conflicting Information" items={fc.conflicting_information} color="text-crimson" />
        <FCList title="Warnings" items={fc.warnings} color="text-crimson" />
      </div>
    );
  }
  const excluded = fc.excluded_claims_used || [];
  return (
    <div className="space-y-3 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="fact-check-verdict" className={`px-2 py-0.5 uppercase tracking-wider font-bold border ${VERDICTS[V] || "bg-amber/15 text-amber border-amber"}`}>{fc.verdict}</span>
        {fc.research_package_received ? <span className="text-emerald text-[10px] uppercase tracking-wider">● Evidence attached</span> : <span className="text-crimson text-[10px] uppercase tracking-wider">● No evidence package</span>}
      </div>
      {fc.summary ? <p className="text-admtext2">{fc.summary}</p> : null}
      <div className="text-admmuted text-[11px]">
        Verified core claims: <span className="text-emerald font-bold">{fc.verified_core_claims_count ?? 0}</span>{typeof fc.core_claims_total === "number" ? ` / ${fc.core_claims_total}` : ""}
        {fc.research_timestamp ? <span className="ml-2">· Research: {new Date(fc.research_timestamp).toLocaleString()}</span> : null}
      </div>

      {fc.core_claims_status?.length ? (
        <div><div className="uppercase tracking-wider font-bold text-emerald mb-1">Core Claims</div>
          <ul className="space-y-1">{fc.core_claims_status.map((c,i)=><ClaimRow key={c.claim || i} claim={c.claim} cls={c.status} badge={c.status} sources={c.sources} />)}</ul>
        </div>) : null}

      {fc.claim_by_claim?.length ? (
        <div><div className="uppercase tracking-wider font-bold text-admtext2 mb-1">Claim-by-Claim Verification</div>
          <ul className="space-y-1">{fc.claim_by_claim.map((c,i)=><ClaimRow key={c.claim || i} claim={c.claim} cls={c.classification} badge={c.classification} sources={c.sources} note={c.note} />)}</ul>
        </div>) : null}

      {fc.new_claims?.length ? (
        <div><div className="uppercase tracking-wider font-bold text-amber mb-1">New Claims Introduced in Draft</div>
          <ul className="space-y-1">{fc.new_claims.map((c,i)=><ClaimRow key={c.claim || i} claim={c.claim} cls={c.classification} badge={(c.action||"").toUpperCase()} note={c.note} />)}</ul>
        </div>) : null}

      <div><div className={`uppercase tracking-wider font-bold mb-1 ${excluded.length ? "text-crimson" : "text-emerald"}`}>Excluded Claims Check</div>
        {excluded.length
          ? <ul className="space-y-1 text-crimson list-disc pl-4">{excluded.map((e,i)=><li key={e.claim || i}><span className="font-bold">EXCLUDED CLAIM USED:</span> {e.claim}{e.excerpt ? <span className="text-admmuted italic"> — “{e.excerpt}”</span> : null}</li>)}</ul>
          : <p className="text-admmuted">No excluded/unverified claims used as fact ✓</p>}
      </div>

      {fc.source_evidence?.length ? (
        <div><div className="uppercase tracking-wider font-bold text-admtext2 mb-1">Source Evidence</div>
          <ul className="space-y-1 text-admmuted">{fc.source_evidence.map((s,i)=><li key={(typeof s === "string" ? s : s.source) || i} className="break-all">{typeof s === "string" ? s : `${s.source}${s.supports?.length ? " → " + s.supports.join("; ") : ""}`}</li>)}</ul>
        </div>) : null}

      <FCList title="Warnings" items={fc.warnings} color="text-crimson" />
      <FCList title="Recommended Corrections" items={fc.recommended_corrections} color="text-amber" />
    </div>
  );
}
